import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ShootingSession } from "../types/ShootingSession";
import { HitDto } from "../types/TargetScannedDataDTOs";
import {
    calculateMeanRadius,
    calculateMaxSpread,
    calculateGroupCenterPosition,
    calculateDistance,
    calculateGroupHeight,
    calculateGroupWidth,
} from "../utils/statsCalculation";
import "./EventRankingSummary.css";

interface SessionStats {
    session: ShootingSession;
    allHits: HitDto[];
    meanRadius: number;
    maxSpread: number;
    groupCenterOffset: number;
    groupWidth: number;
    groupHeight: number;
}

interface AwardCardProps {
    icon: string;
    title: string;
    winner: string;
    value: string;
    color: string;
    sessionId: string;
    competitionName?: string;
    competitionDate?: string;
}

function AwardCard({ icon, title, winner, value, color, sessionId, competitionName, competitionDate }: AwardCardProps) {
    const navigate = useNavigate();
    return (
        <div
            className="award-card"
            style={{ borderTopColor: color, cursor: "pointer" }}
            onClick={() => navigate(`/session-reports/${sessionId}`, {
                state: {
                    targetOwnerName: winner,
                    competitionName,
                    competitionDate,
                },
            })}
        >
            <div className="award-icon">{icon}</div>
            <div className="award-title">{title}</div>
            <div className="award-winner">{winner}</div>
            <div className="award-value" style={{ color }}>{value}</div>
        </div>
    );
}

interface EventRankingSummaryProps {
    sessions: ShootingSession[];
    competitionName?: string;
    competitionDate?: string;
}

function EventRankingSummary({ sessions, competitionName, competitionDate }: EventRankingSummaryProps) {
    const stats = useMemo<SessionStats[]>(() => {
        return sessions
            .map((session) => {
                const allHits = session.targetResults.flatMap(
                    (tr) => tr.coordinatesOfHitsRelativeToCenter
                );
                if (allHits.length === 0) return null;

                const meanRadius = calculateMeanRadius(allHits);
                const maxSpread = calculateMaxSpread(allHits);
                const groupCenter = calculateGroupCenterPosition(allHits);
                const groupCenterOffset = calculateDistance(groupCenter, { x: 0, y: 0 });
                const groupWidth = calculateGroupWidth(allHits);
                const groupHeight = calculateGroupHeight(allHits);

                return { session, allHits, meanRadius, maxSpread, groupCenterOffset, groupWidth, groupHeight };
            })
            .filter((s): s is SessionStats => s !== null);
    }, [sessions]);

    if (stats.length === 0) return null;

    const bestMeanRadius = stats.reduce((best, s) =>
        s.meanRadius < best.meanRadius ? s : best
    );
    const bestCentering = stats.reduce((best, s) =>
        s.groupCenterOffset < best.groupCenterOffset ? s : best
    );
    const minimalMaxSpread = stats.reduce((best, s) =>
        s.maxSpread < best.maxSpread ? s : best
    );
    const minGroupWidth = stats.reduce((best, s) =>
        s.groupWidth < best.groupWidth ? s : best
    );
    const minGroupHeight = stats.reduce((best, s) =>
        s.groupHeight < best.groupHeight ? s : best
    );

    const fmt = (val: number) => val.toFixed(2);

    return (
        <div className="ranking-summary-section">
            <h5 className="ranking-summary-heading">Wyróżnienia</h5>
            <div className="award-cards-grid">
                <AwardCard
                    icon="🗜️"
                    title="Najciaśniejsze skupienie"
                    winner={bestMeanRadius.session.owner}
                    value={`${fmt(bestMeanRadius.meanRadius)} mm`}
                    color="#e5a100"
                    sessionId={bestMeanRadius.session.id}
                    competitionName={competitionName}
                    competitionDate={competitionDate}
                />
                <AwardCard
                    icon="🎯"
                    title="Najbardziej centralne skupienie"
                    winner={bestCentering.session.owner}
                    value={`${fmt(bestCentering.groupCenterOffset)} mm od środka`}
                    color="#2e86de"
                    sessionId={bestCentering.session.id}
                    competitionName={competitionName}
                    competitionDate={competitionDate}
                />
                <AwardCard
                    icon="📏"
                    title="Najmniejszy maksymalny rozrzut"
                    winner={minimalMaxSpread.session.owner}
                    value={`${fmt(minimalMaxSpread.maxSpread)} mm`}
                    color="#10ac84"
                    sessionId={minimalMaxSpread.session.id}
                    competitionName={competitionName}
                    competitionDate={competitionDate}
                />
                <AwardCard
                    icon="←→"
                    title="Najmniejszy rozrzut w poziomie"
                    winner={minGroupWidth.session.owner}
                    value={`${fmt(minGroupWidth.groupWidth)} mm`}
                    color="#ee5253"
                    sessionId={minGroupWidth.session.id}
                    competitionName={competitionName}
                    competitionDate={competitionDate}
                />
                <AwardCard
                    icon="↑↓"
                    title="Najmniejszy rozrzut w pionie"
                    winner={minGroupHeight.session.owner}
                    value={`${fmt(minGroupHeight.groupHeight)} mm`}
                    color="#9b59b6"
                    sessionId={minGroupHeight.session.id}
                    competitionName={competitionName}
                    competitionDate={competitionDate}
                />
            </div>
        </div>
    );
}

export default EventRankingSummary;
